import { FC, PropsWithChildren, useCallback, useMemo } from 'react';
import {
  generatePath,
  matchRoutes,
  RouteObject,
  useLocation,
  useNavigate,
  useRoutes,
} from 'react-router-dom';
import { Main } from '@atlas-frontend/sdk';
import { Menu, MenuProps } from 'antd';
import { MenuItemType } from 'antd/es/menu/interface';
import styled from 'styled-components';
import { onBasePath } from '../paths/onBasePath';
import { SectionObject } from './types';
import { SectionContextProvider } from './SectionContext';

export interface SectionProps {
  sections: SectionObject[];
  basePath?: string;
}

export const Section: FC<SectionProps> = (props) => {
  const { sections, basePath = '' } = props;
  const { pathname } = useLocation();
  const routes = useMemo(
    () =>
      onBasePath(
        basePath,
        sections
          .map((s) => s.route)
          .filter((route): route is RouteObject => !!route),
      ),
    [basePath, sections],
  );

  const sectionMatches = useMemo(
    () =>
      matchRoutes(
        routes,
        basePath ? pathname.replace(new RegExp(`^${basePath}`), '') : pathname,
      )?.filter(({ route }) => !route.index),
    [routes, pathname, basePath],
  );

  const sectionIndexMatches = useMemo(
    () => sectionMatches?.filter(({ params }) => !params['*']),
    [sectionMatches],
  );

  const sectionRoutes = useRoutes(routes);
  const selectedKeys = useMemo(
    () =>
      (sectionIndexMatches || []).reduce<string[]>((keys, { route }) => {
        const key = sections
          .find((s) => s?.route?.path === basePath + route.path)
          ?.key.toString();
        return key ? keys.concat(key) : keys;
      }, []),
    [sectionIndexMatches, sections],
  );

  return (
    <SectionContextProvider
      basePath={
        basePath +
        (sectionMatches && sectionMatches.length
          ? sectionMatches[0].pathnameBase
          : '')
      }
    >
      {sectionIndexMatches?.length ? (
        <SectionIndexWithMenu sections={sections} selectedKeys={selectedKeys}>
          {sectionRoutes}
        </SectionIndexWithMenu>
      ) : (
        sectionRoutes
      )}
    </SectionContextProvider>
  );
};

interface SectionIndexWithMenuProps extends PropsWithChildren<SectionProps> {
  selectedKeys: string[];
}

export const SectionIndexWithMenu: FC<SectionIndexWithMenuProps> = (props) => {
  const { sections, selectedKeys, children } = props;

  const items = useMemo(
    () =>
      sections.reduce<MenuItemType[]>(
        (items, { key, hiddenFromMenu, ...rest }) =>
          hiddenFromMenu
            ? items
            : items.concat({
                key: key.toString(),
                ...rest,
              }),
        [],
      ),
    [sections],
  );

  const navigate = useNavigate();

  const handleSelect: Required<MenuProps>['onSelect'] = useCallback(
    ({ key }) => {
      const section = sections.find((s) => s.key === key);
      if (section?.route?.path) {
        navigate(generatePath(section.route.path));
      }
    },
    [sections, navigate],
  );

  return (
    <Main>
      <Main.Col>
        <StyledMainHeader>
          <StyledMenu
            items={items}
            selectedKeys={selectedKeys}
            onSelect={handleSelect}
            mode="horizontal"
            disabledOverflow
          />
        </StyledMainHeader>
        <Main.Content padding={false} scrollable={false}>
          {children}
        </Main.Content>
      </Main.Col>
    </Main>
  );
};

const StyledMainHeader = styled(Main.Header)`
  justify-content: center;
`;

const StyledMenu = styled(Menu)`
  border-bottom: none;
`;
